import * as React from "react";
import { classes, style } from "typestyle";
import { History } from "history";
import { getLoginService } from "../../login-service/LoginService";
import { Throbber } from "../Throbber";
import { i18nKeys, t } from "../../i18n/i18n";
import {
  getErrorMessage,
  LoginServiceError,
  LoginServiceErrorCode
} from "../../login-service/LoginServiceErrors";
import redirectToApp from "../redirect-to-app";
import redirectToWelcomePage from "../redirect-to-welcome";
import trackAction from "../../libs/analytics/SigninPageAnalytics";
import {
  Alert,
  Button,
  ButtonsContainer,
  TextInput,
  TextItem,
  WindowBody,
  WindowVerticalLayout,
  Window,
  typeCompact24Display
} from "@traceair/tds";
import PasswordInput from "../forms/PasswordInput";
import { BigLogo } from "../BigLogo";
import {
  descriptionStyle,
  greetingStyle,
  headerStyle,
  windowStyle
} from "./styles";
import { AlertIcon } from "@traceair/webapp-icons";
import { interpolateTemplate, pluralize } from "../../helpers";

interface ISigninPageProps {
  history: History;
  refererUrl: string;
  defaultEmail?: string;
  accessToken?: string;
}

interface ILoginPageState {
  pending: boolean;
  warningMessageTitle: any;
  warningMessageSubTitle?: any;
  email: string;
  password: string;
  emailWarningMessage: string;
  passwordWarningMessage: string;
  waitForNewAttemptTimeout: ReturnType<typeof setTimeout> | null;
}

export class SigninPage extends React.Component<
  ISigninPageProps,
  ILoginPageState
> {
  constructor(props: ISigninPageProps) {
    super(props);

    this.state = {
      pending: false,
      warningMessageTitle: "",
      warningMessageSubTitle: "",
      email: this.props.defaultEmail || "",
      password: "",
      emailWarningMessage: "",
      passwordWarningMessage: "",
      waitForNewAttemptTimeout: null
    };

    this.checkUserNonActivated();
    trackAction("Login page is opened");
  }

  async componentDidMount() {
    try {
      await getLoginService().ensureUserLoggedIn();
    } catch (err) {
      const response = err.response;
      if (
        response &&
        response.status === 403 &&
        response.data.code === "NO_TERMS_OF_USE_AGREEMENT"
      ) {
        this.props.history.push("/terms-of-use");
        return;
      }
      throw err;
    }
  }

  componentWillUnmount(): void {
    if (this.state.waitForNewAttemptTimeout) {
      clearInterval(this.state.waitForNewAttemptTimeout);
    }
  }

  validateEmail = (email: string) => {
    this.setState({ emailWarningMessage: "" });

    if (!email) {
      this.setState({ emailWarningMessage: t(i18nKeys.emailRequired) });
      return false;
    }

    if (!emailRegExp.test(email)) {
      this.setState({ emailWarningMessage: t(i18nKeys.incorrectEmail) });
      return false;
    }
    return true;
  };

  validatePassword(password: string) {
    this.setState({ passwordWarningMessage: "" });
    if (!password) {
      this.setState({ passwordWarningMessage: t(i18nKeys.passwordRequired) });
      return false;
    }
    return true;
  }

  checkUserNonActivated() {
    const { accessToken } = this.props;
    if (!accessToken) {
      return;
    }

    getLoginService()
      .ensureAccessTokenExists(accessToken)
      .then(({ exists }) => {
        if (exists) {
          this.redirectToWelcomePage();
        }
      });
  }

  handleTooManyAttemptsError = (error: LoginServiceError) => {
    const { title, subTitle } = getErrorMessage(error);
    let retryAfter = error.data?.retryAfter ?? 0;

    if (!retryAfter) return;

    const waitForNewAttemptTimeout = setInterval(() => {
      retryAfter--;

      if (retryAfter <= 0 && this.state.waitForNewAttemptTimeout) {
        clearInterval(this.state.waitForNewAttemptTimeout);
        this.setState({
          warningMessageTitle: "",
          warningMessageSubTitle: "",
          waitForNewAttemptTimeout: null
        });
        return;
      }

      this.setState({
        warningMessageTitle: title,
        warningMessageSubTitle: interpolateTemplate(String(subTitle), {
          s: pluralize(retryAfter, "second", "seconds")
        })
      });
    }, 1000);

    this.setState({
      warningMessageTitle: title,
      warningMessageSubTitle: interpolateTemplate(String(subTitle), {
        s: pluralize(retryAfter, "second", "seconds")
      }),
      waitForNewAttemptTimeout
    });
  };

  handleSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();
    this.setState({ pending: true });
    const { email, password } = this.state;
    const isPasswordCorrect = this.validatePassword(password);
    const isEmailCorrect = this.validateEmail(email);
    if (!isEmailCorrect || !isPasswordCorrect) {
      this.setState({ pending: false });
      return;
    }

    trackAction("Login page - submit data");

    getLoginService()
      .login(email, password)
      .catch((error: LoginServiceError) => {
        this.setState({ pending: false });
        if (error.code === LoginServiceErrorCode.TOU_NOT_AGREED) {
          this.props.history.push("/terms-of-use");
        } else if (error.code === LoginServiceErrorCode.TOO_MANY_ATTEMPTS) {
          this.handleTooManyAttemptsError(error);
        } else {
          const { title, subTitle } = getErrorMessage(error);
          this.setState({
            warningMessageTitle: title,
            warningMessageSubTitle: subTitle
          });

          trackAction("Login page - wrong data entered");
        }

        throw error;
      })
      .then(() => {
        this.setState({ warningMessageTitle: "", warningMessageSubTitle: "" });
        trackAction("Login page - successful login");
        this.redirectToApp();
      });
  };

  redirectToApp() {
    let { refererUrl } = this.props;
    redirectToApp(refererUrl);
  }

  redirectToWelcomePage = () => {
    let { refererUrl, accessToken, defaultEmail } = this.props;
    if (!accessToken || !defaultEmail) {
      return;
    }
    redirectToWelcomePage(refererUrl, accessToken, defaultEmail);
  };

  handleForgetPasswordButtonClick = () => {
    this.props.history.push("/recovery");
  };

  render() {
    const { pending } = this.state;
    return (
      <Window className={windowStyle} header={this.renderHeader()}>
        <WindowBody className={bodyOverflowStyle}>
          <form onSubmit={this.handleSubmit}>
            <Throbber pending={pending}>
              <WindowVerticalLayout>
                {this.renderWarningMessage()}
                {this.renderEmailInput()}
                {this.renderPasswordInput()}
                <div className={forgotPasswordButtonContainerStyle}>
                  <Button
                    style={"tertiary"}
                    text={t(i18nKeys.dontKnowPassword)}
                    onClick={this.handleForgetPasswordButtonClick}
                    className={forgotPasswordButtonStyle}
                  />
                </div>
              </WindowVerticalLayout>
            </Throbber>
            {this.renderFooter()}
          </form>
        </WindowBody>
      </Window>
    );
  }

  renderHeader = () => {
    return (
      <div className={headerStyle}>
        <BigLogo />
        {this.renderGreeting()}
      </div>
    );
  };

  renderWarningMessage = () => {
    const { warningMessageTitle, warningMessageSubTitle } = this.state;
    if (!warningMessageTitle) {
      return null;
    }
    return (
      <Alert
        icon={AlertIcon}
        type={"warning"}
        title={warningMessageTitle}
        description={warningMessageSubTitle}
      />
    );
  };

  renderGreeting() {
    return (
      <div className={greetingStyle}>
        <TextItem
          text={t(i18nKeys.signInGreetingHeader)}
          style={typeCompact24Display}
        />
        <div className={descriptionStyle}>
          {t(i18nKeys.signInGreetingDescription)}
        </div>
      </div>
    );
  }

  renderFooter() {
    return (
      <ButtonsContainer>
        <Button
          disabled={Boolean(this.state.waitForNewAttemptTimeout)}
          className="login-button"
          text={t(i18nKeys.signIn)}
          style="primary"
          fullWidth
          htmlType="submit"
        />
      </ButtonsContainer>
    );
  }

  renderEmailInput() {
    const { email, waitForNewAttemptTimeout } = this.state;
    const onChange = (email: string) => {
      this.setState({ email });
    };
    return (
      <TextInput
        autoFocus
        label="Email"
        disabled={Boolean(waitForNewAttemptTimeout)}
        placeholder={t(i18nKeys.emailPlaceholder)}
        onChange={onChange}
        onBlur={() => {
          this.validateEmail(email);
        }}
        className="email"
        value={email}
        warningMessage={this.state.emailWarningMessage}
        isWarning={!!this.state.emailWarningMessage}
        hidePlaceholderOnFocus
      />
    );
  }

  renderPasswordInput() {
    const { password, waitForNewAttemptTimeout } = this.state;
    const onChange = (password: string) => {
      this.setState({ password });
    };
    return (
      <PasswordInput
        className={classes("password")}
        onChange={onChange}
        disabled={Boolean(waitForNewAttemptTimeout)}
        onBlur={() => {
          this.validatePassword(password);
        }}
        value={this.state.password}
        placeholder={t(i18nKeys.password)}
        title={t(i18nKeys.password)}
        isWarning={!!this.state.passwordWarningMessage}
        warningMessage={this.state.passwordWarningMessage}
      />
    );
  }
}

export const emailRegExp =
  /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/i;

const forgotPasswordButtonContainerStyle = style({
  paddingTop: 16
});

const forgotPasswordButtonStyle = style({
  width: "fit-content",
  height: "auto",
  padding: 0,

  $nest: {
    "&:hover": {
      background: "transparent !important"
    }
  }
});

const bodyOverflowStyle = style({
  overflowX: "hidden"
});
